// src/components/popups/StakeDrawer.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './StakeDrawer.css';
import api from '../../api';
import closeIcon from '../../assets/close-icon.png';
import { useTranslation } from 'react-i18next';

import QuickTopUpPopup from './QuickTopUpPopup';

const MIN_PI = 1;

// Fixed tiers and exact fee % — mirrors backend config
const TIERS = [
  { hours: 4, label: '4h', feePercent: 4 },
  { hours: 12, label: '12h', feePercent: 8 },
  { hours: 24, label: '24h', feePercent: 16 },
  { hours: 36, label: '36h', feePercent: 27 },
  { hours: 48, label: '48h', feePercent: 40 },
];

function isTopUpRequiredError(e) {
  const data = e?.response?.data || {};
  const code = String(data?.errorCode || data?.code || '').toUpperCase();
  const action = data?.action;
  const msg = String(data?.message || data?.error || e?.message || '').toLowerCase();

  return (
    code === 'INSUFFICIENT_PI' ||
    code === 'WALLET_NOT_FOUND' ||
    code === 'INSUFFICIENT_BALANCE' ||
    code === 'WALLET_INSUFFICIENT_BALANCE' ||
    code === 'INSUFFICIENT_FUNDS' ||
    action === 'TOP_UP_REQUIRED' ||
    msg.includes('insufficient') ||
    msg.includes('not enough pi') ||
    msg.includes('no active barterfi wallet') ||
    msg.includes('wallet not found') ||
    msg.includes('balance') ||
    msg.includes('insufficient funds')
  );
}

export default function StakeDrawer({
  isOpen,
  onClose,
  postId,
  // defaultAmount is intentionally ignored to keep the input empty on open
  defaultAmount,
  onSuccess,
  currentUser,
}) {
  const { t } = useTranslation();

  const [amountPi, setAmountPi] = useState('');
  const [tierIndex, setTierIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [amountErr, setAmountErr] = useState('');

  // ✅ success state + auto-close timer
  const [created, setCreated] = useState(null);
  const closeTimerRef = useRef(null);

  // ✅ Quick top-up state
  const [showQuickTopUp, setShowQuickTopUp] = useState(false);
  const quickTopUpTimerRef = useRef(null);

  const openQuickTopUpAfterDelay = () => {
    if (quickTopUpTimerRef.current) {
      clearTimeout(quickTopUpTimerRef.current);
    }

    quickTopUpTimerRef.current = window.setTimeout(() => {
      setShowQuickTopUp(true);
    }, 2500);
  };

  useEffect(() => {
    if (isOpen) {
      setErr('');
      setAmountErr('');
      setAmountPi(''); // keep empty, no prefill
      setTierIndex(0);
      setCreated(null);
      setShowQuickTopUp(false);

      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      if (quickTopUpTimerRef.current) {
        clearTimeout(quickTopUpTimerRef.current);
        quickTopUpTimerRef.current = null;
      }
    }

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      if (quickTopUpTimerRef.current) {
        clearTimeout(quickTopUpTimerRef.current);
        quickTopUpTimerRef.current = null;
      }
    };
  }, [isOpen]);

  const numAmount = useMemo(() => Number(amountPi), [amountPi]);
  const amountInvalid = !amountPi || !Number.isFinite(numAmount) || numAmount <= 0;
  const amountTooLow = Number.isFinite(numAmount) && numAmount > 0 && numAmount < MIN_PI;

  useEffect(() => {
    if (amountInvalid) {
      setAmountErr('');
    } else if (amountTooLow) {
      setAmountErr(
        t('stake.inline.min_amount', {
          min: MIN_PI,
          defaultValue: `Minimum ${MIN_PI} Pi`,
        })
      );
    } else {
      setAmountErr('');
    }
  }, [amountInvalid, amountTooLow, t]);

  const selected = TIERS[Math.max(0, Math.min(TIERS.length - 1, tierIndex))];

  const feePi = useMemo(() => {
    if (!Number.isFinite(numAmount)) return 0;
    return +(numAmount * (selected.feePercent / 100)).toFixed(4);
  }, [numAmount, selected.feePercent]);

  const refundablePi = useMemo(() => {
    if (!Number.isFinite(numAmount)) return 0;
    return +(numAmount - feePi).toFixed(4);
  }, [numAmount, feePi]);

  // 🔓 Public: no admin gate
  const disabled = useMemo(() => {
    return !postId || loading || amountInvalid || amountTooLow || !!created;
  }, [postId, loading, amountInvalid, amountTooLow, created]);

  function unlocksAtString(hours = selected.hours) {
    const d = new Date(Date.now() + hours * 3600 * 1000);
    const pad = (n) => String(n).padStart(2, '0');

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  // Map API error → short UI message
  function extractStakeError(e) {
    const status = e?.response?.status;
    const data = e?.response?.data || {};
    const code = String(data?.errorCode || data?.code || '').toUpperCase();
    const msg = String(data?.message || data?.error || e?.message || '');

    if (isTopUpRequiredError(e)) {
      return t('stake.errors.insufficient_funds', {
        defaultValue: 'Insufficient Pi',
      });
    }

    if (code === 'INSUFFICIENT_FUNDS') {
      return t('stake.errors.insufficient_funds', {
        defaultValue: 'Insufficient funds',
      });
    }

    if (/insufficient funds/i.test(msg)) {
      return t('stake.errors.insufficient_funds', {
        defaultValue: 'Insufficient funds',
      });
    }

    if (status === 400 && (data?.message || data?.error)) {
      return String(data.message || data.error);
    }

    if (status === 500 && /wallet/i.test(msg)) {
      return t('stake.errors.create_failed', {
        defaultValue: 'Failed to create stake.',
      });
    }

    return t('stake.errors.create_failed', {
      defaultValue: 'Failed to create stake.',
    });
  }

  const handleCreate = async () => {
    setErr('');
    if (disabled) return;

    setLoading(true);

    try {
      const payload = {
        staker: String(currentUser || '').trim(),
        amountPi: numAmount,
        durationHours: selected.hours,
      };

      const res = await api.post(`/posts/${postId}/stakes`, payload);

      if (!res?.data?.stake) {
        throw new Error('No stake returned');
      }

      // 🌟 Show success card (90% width) before closing
      const successData = {
        amount: numAmount,
        fee: feePi,
        refundable: refundablePi,
        label: selected.label,
        unlocksAt: unlocksAtString(selected.hours),
      };

      setCreated(successData);

      // Auto-close after a short celebratory pause
      closeTimerRef.current = setTimeout(() => {
        onSuccess?.(res.data);
        onClose?.();
        setCreated(null);
      }, 3000);
    } catch (e) {
      console.error('[StakeDrawer] error:', e);

      if (isTopUpRequiredError(e)) {
        setErr(
          t('stake.errors.insufficient_funds', {
            defaultValue: 'Insufficient Pi',
          })
        );

        openQuickTopUpAfterDelay();
        return;
      }

      setErr(extractStakeError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={`stake-drawer ${isOpen ? 'open' : ''}`} aria-hidden={!isOpen}>
        <div className="stake-drawer__backdrop" onClick={onClose} />

        <div className="stake-drawer__panel" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="stake-drawer__header">
            <div className="stake-drawer__title">
              {t('stake.title', { defaultValue: 'Stake to Boost' })}
            </div>

            <button
              className="stake-drawer__close"
              onClick={onClose}
              aria-label={t('stake.buttons.close', { defaultValue: 'Close' })}
              disabled={!!created}
            >
              <img
                src={closeIcon}
                alt={t('stake.buttons.close', { defaultValue: 'Close' })}
              />
            </button>
          </div>

          {/* ✅ Success card (appears at the top, 90% width) */}
          {created && (
            <div className="stake-drawer__success" role="status" aria-live="polite">
              <div className="success-card">
                <div className="success-icon" aria-hidden="true">
                  {/* inline check svg to avoid extra assets */}
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <circle cx="12" cy="12" r="12" opacity="0.15" />
                    <path
                      d="M6 12.5l3.5 3.5L18 8.5"
                      fill="none"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                <div className="success-body">
                  <div className="success-title">
                    {t('stake.success.title', {
                      defaultValue: 'Stake created 🎉',
                    })}
                  </div>

                  <div className="success-sub">
                    {t('stake.success.subtitle', {
                      amount: created.amount.toFixed(2),
                      label: created.label,
                      defaultValue: `You staked ${created.amount.toFixed(
                        2
                      )} Pi for ${created.label}.`,
                    })}
                  </div>

                  <div className="success-meta">
                    <span>
                      {t('stake.preview.refundable', {
                        defaultValue: 'Refundable',
                      })}
                      : {created.refundable.toFixed(4)} Pi
                    </span>

                    <span> • </span>

                    <span>
                      {t('stake.preview.unlocks', {
                        defaultValue: 'Unlocks',
                      })}
                      : {created.unlocksAt}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Form is dimmed/disabled while success card is visible */}
          <div className={`stake-drawer__content ${created ? 'dimmed' : ''}`}>
            {/* Amount */}
            <div className="stake-drawer__row">
              <label>
                {t('stake.amount_label', { defaultValue: 'Amount (Pi)' })}
              </label>

              <input
                type="number"
                min={MIN_PI}
                step="0.1"
                value={amountPi}
                onChange={(e) => setAmountPi(e.target.value)}
                placeholder={t('stake.amount_placeholder', {
                  min: MIN_PI,
                  defaultValue: `amount (Pi) · min ${MIN_PI}`,
                })}
                inputMode="decimal"
                disabled={!!created}
              />

              {amountErr && (
                <div className="stake-drawer__inline-error">{amountErr}</div>
              )}
            </div>

            {/* Duration line selector */}
            <div className="stake-drawer__row">
              <label>
                {t('stake.duration_label', { defaultValue: 'Duration' })}
              </label>

              <div className="stake-drawer__range-wrap">
                <input
                  type="range"
                  min="0"
                  max={TIERS.length - 1}
                  step="1"
                  value={tierIndex}
                  onChange={(e) => setTierIndex(Number(e.target.value))}
                  className="stake-drawer__range"
                  disabled={!!created}
                />

                <div className="stake-drawer__ticks">
                  {TIERS.map((tTier, i) => (
                    <div
                      key={tTier.hours}
                      className={`tick ${i === tierIndex ? 'active' : ''}`}
                    >
                      <span>{tTier.label}</span>
                      <em className="fee">{tTier.feePercent}%</em>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Live fee preview */}
            <div className="stake-drawer__preview">
              <div className="preview__row">
                <span className="k">
                  {t('stake.preview.selected', { defaultValue: 'Selected' })}
                </span>

                <span className="v">
                  {selected.label}{' '}
                  (
                  {t('stake.preview.fee_pct', {
                    pct: selected.feePercent,
                    defaultValue: `${selected.feePercent}% fee`,
                  })}
                  )
                </span>
              </div>

              <div className="preview__row">
                <span className="k">
                  {t('stake.preview.stake', { defaultValue: 'Stake' })}
                </span>

                <span className="v">
                  {Number.isFinite(numAmount) ? numAmount.toFixed(2) : '--'} Pi
                </span>
              </div>

              <div className="preview__row">
                <span className="k">
                  {t('stake.preview.fee_now', { defaultValue: 'Fee (now)' })}
                </span>

                <span className="v">
                  {Number.isFinite(numAmount) ? feePi.toFixed(4) : '--'} Pi
                </span>
              </div>

              <div className="preview__row">
                <span className="k">
                  {t('stake.preview.refundable', {
                    defaultValue: 'Refundable',
                  })}
                </span>

                <span className="v">
                  {Number.isFinite(numAmount) ? refundablePi.toFixed(4) : '--'} Pi
                </span>
              </div>

              <div className="preview__row">
                <span className="k">
                  {t('stake.preview.unlocks', { defaultValue: 'Unlocks' })}
                </span>

                <span className="v">{unlocksAtString()}</span>
              </div>
            </div>

            {err && <div className="stake-drawer__error">{err}</div>}

            <button
              className="stake-drawer__submit"
              disabled={disabled}
              onClick={handleCreate}
            >
              {loading
                ? t('stake.buttons.creating', {
                    defaultValue: 'Creating…',
                  })
                : t('stake.buttons.stake_cta', {
                    amount: Number.isFinite(numAmount) ? numAmount.toFixed(2) : '',
                    defaultValue: Number.isFinite(numAmount)
                      ? `Stake ${numAmount.toFixed(2)} Pi`
                      : 'Stake',
                  })}
            </button>

            <div className="stake-drawer__hint">
              {t('stake.hint', {
                defaultValue:
                  'You can only withdraw the refundable amount after unlock time.',
              })}
            </div>
          </div>
        </div>
      </div>

      {showQuickTopUp && (
        <QuickTopUpPopup
          open={showQuickTopUp}
          user={{ username: currentUser }}
          minAmount={0.1}
          title="Insufficient Pi"
          subtitle="Top up your BarterFi wallet to stake and boost this post."
          onClose={() => setShowQuickTopUp(false)}
          onSuccess={() => {
            setShowQuickTopUp(false);
            setErr(
              t('stake.topup.success', {
                defaultValue:
                  'Wallet topped up successfully. You can try staking again.',
              })
            );
          }}
        />
      )}
    </>
  );
}
